import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from 'services/api';

const AuthContext = createContext(null);

// D8: si /me dice que la cuenta fue desactivada, se cierra la sesión y el login
// muestra el motivo (antes el propietario seguía con su sesión hasta que vencía).
const AVISO_LOGIN = 'eventify_aviso_login';
const cuentaDesactivada = (err) => err?.response?.status === 403 && err.response.data?.code === 'ACCOUNT_DEACTIVATED';
const cerrarPorDesactivada = (err) => {
  try { sessionStorage.setItem(AVISO_LOGIN, err.response.data.message); } catch (_) {}
  localStorage.removeItem('token');
  localStorage.removeItem('user');
};

export const AuthProvider = ({ children }) => {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);

  // Re-consulta /me y actualiza el user del contexto + localStorage. El
  // endpoint de login devuelve un user reducido (sin phone, etc.); esto trae
  // el objeto completo. Se puede llamar desde cualquier pantalla que necesite
  // datos frescos del propio usuario (ej. el panel del propietario).
  const refreshUser = useCallback(async () => {
    try {
      const { data } = await authService.me();
      const userData = { ...data, role: data.role || data.roles?.[0]?.name || 'user' };
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      return userData;
    } catch (err) {
      if (cuentaDesactivada(err)) { cerrarPorDesactivada(err); setUser(null); window.location.href = '/login'; }
      return null;
    }
  }, []);

  useEffect(() => {
    const token      = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (!token) { setLoading(false); return; }

    if (storedUser) {
      try { setUser(JSON.parse(storedUser)); } catch (_) {}
    }

    authService.me()
      .then(({ data }) => {
        const userData = { ...data, role: data.role || data.roles?.[0]?.name || 'user' };
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      })
      .catch((err) => {
        if (cuentaDesactivada(err)) cerrarPorDesactivada(err);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await authService.login({ email, password });
    localStorage.setItem('token', data.token);
    // El endpoint de login devuelve un `user` reducido (sin phone ni otros
    // campos que solo trae /me). Sin esto, tras un login "fresco" el efecto
    // de arriba no vuelve a correr y el usuario se queda toda la sesión con
    // un objeto incompleto (ej. `phone` undefined → el aviso "confirma tu
    // teléfono" salía aunque sí lo tuviera). Se completa con /me de una vez.
    let fullUser = { ...data.user };
    try {
      const me = await authService.me();
      fullUser = { ...me.data, role: me.data.role || data.user.role || 'user' };
    } catch (_) { /* si /me falla, se sigue con el user del login */ }
    localStorage.setItem('user', JSON.stringify(fullUser));
    setUser(fullUser);
    return fullUser;
  };

  // D4: inicia la sesión con un token ya emitido (registro del propietario).
  // Antes Register.jsx guardaba el token en localStorage sin avisar al contexto:
  // al ir a /dashboard la app no veía sesión y lo mandaba al login. (Reemplaza
  // un `register` viejo que nadie usaba y que mandaba campos que la API ya no recibe.)
  const iniciarSesionConToken = async (token, userInicial) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userInicial));
    setUser(userInicial);
    await refreshUser(); // trae el perfil completo (teléfono, etc.), igual que login
  };

  const logout = async () => {
    try { await authService.logout(); } catch (_) {}
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const isAdmin         = ['admin', 'super_admin'].includes(user?.role);
  const isSuperAdmin    = user?.role === 'super_admin';
  // Un admin cuya propiedad está en el plan "Asamblea" (2 semanas, 1 asamblea)
  // sigue siendo un admin de verdad — el plan limita cuántas asambleas puede
  // crear, no sus capacidades. Antes se le trataba igual que a un
  // assembly_admin real (cuenta ayudante limitada a UNA asamblea asignada vía
  // user.assembly_id) y perdía el menú completo Y el selector de propiedad/
  // asamblea en Preguntas/Asistentes (que se oculta para assembly_admin real
  // porque su asamblea carga sola) — como un admin normal no tiene
  // assembly_id, se quedaba sin selector Y sin auto-carga, totalmente
  // atascado. isAssemblyAdmin ahora refleja únicamente el rol real.
  const isAssemblyAdmin  = user?.role === 'assembly_admin';
  const isLogisticaAdmin = user?.role === 'admin' && user?.subscription_type === 'logistica';
  const isTrial          = user?.role === 'admin' && user?.subscription_status === 'trial';

  // ── Helper: calcular estado de suscripción a partir de status + expires ─────
  // Reutilizable para la propiedad del usuario o cualquier propiedad específica
  const calcSubscriptionState = (status, expires, loadingState = false) => {
    if (loadingState) return 'none';
    if (expires) {
      const expDate   = new Date(expires);
      const now       = new Date();
      const daysSince = Math.floor((now - expDate) / (1000 * 60 * 60 * 24));
      if (expDate > now) return status === 'trial' ? 'trial' : 'active';
      if (daysSince <= 14) return 'grace';
      return 'blocked';
    }
    if (!status || status === 'inactive') return 'blocked';
    return status === 'trial' ? 'trial' : 'active';
  };

  // ── Estado real de suscripción para el admin (su propiedad principal) ─────
  const subscriptionState = (() => {
    if (loading) return 'none';
    if (user?.role !== 'admin') return 'none';
    return calcSubscriptionState(user?.subscription_status, user?.subscription_expires_at);
  })();

  const isReadOnly = subscriptionState === 'grace';

  // ── Estado de suscripción para una propiedad específica ───────────────────
  // Recibe un objeto property con { subscription_status, subscription_expires_at }
  // Útil para admin con múltiples propiedades: calcular estado por propiedad seleccionada
  const getSubscriptionStateForProperty = (property) => {
    if (!property) return 'none';
    if (user?.role !== 'admin') return 'none';
    return calcSubscriptionState(
      property.subscription_status,
      property.subscription_expires_at
    );
  };

  return (
    <AuthContext.Provider value={{
      user, setUser, loading,
      login, iniciarSesionConToken, logout, refreshUser,
      isAdmin, isSuperAdmin, isAssemblyAdmin, isLogisticaAdmin, isTrial,
      subscriptionState, isReadOnly,
      getSubscriptionStateForProperty,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};